import {
  Typography,
  useTheme,
  TextField,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Tooltip,
} from '@mui/material';
import {
  type GridRowData,
  type GridColumns,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { CustomDataGrid, DialogBox } from 'components';
import { colorTheme } from 'theme';
import { useEffect, useState } from 'react';
import { viewAllTransactionDetails } from 'service';
import {
  fundTypeOption,
  orderTypeOption,
  transactionDetailsConstants,
  transStatusTypeOption,
} from 'constants/constantText';
import { strings } from 'i18n/Strings';
import {
  checkSortingValues,
  dateFormat,
  UnitFormater,
} from 'utils/GeneralUtil';
import AllocationDetails from './AllocationDetails';
import { useSelector, useDispatch } from 'react-redux';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import { LoadingButton } from '@mui/lab';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';
import { getFundType } from 'utils/Constants';

export interface mmTransactionInterface {
  mmOrderTransactionId: number;
  totalContribution: number;
  transactionMethod: string;
  transactionStatus: string;
  transactionDate: string;
}

const TransactionDetails = (): JSX.Element => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const colors = colorTheme(theme.palette.mode);
  const [planData, setPlanData] = useState<mmTransactionInterface[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pageCount, setPageCount] = useState(null);
  const pageInfo = useSelector((state) => state?.pagination?.mmTransaction);
  const [filters, setFilters] = useState<unknown>({
    startDate: null,
    endDate: null,
    fStartDate: null,
    fEndDate: null,
    sStartDate: null,
    sEndDate: null,
    status: '',
    fundType: '',
    orderType: '',
  });
  const [searchValue, setSearchValue] = useState('');

  const handleFetchTransDetails = async (): Promise<void> => {
    void viewAllTransactionDetails(
      Number(pageInfo?.page) + 1,
      pageInfo?.pageSize,
      pageInfo?.sortBy,
      pageInfo?.sortOrder,
      searchValue,
      filters,
    )
      .then((res) => {
        setPlanData(res?.rows);
        setPageCount(res?.count);
      })
      .catch((err) => {
        console.log('error viewAllTransactionDetails', err);
      });
  };
  useEffect(() => {
    void handleFetchTransDetails();
  }, [
    pageInfo?.page,
    pageInfo?.pageSize,
    searchValue,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
  ]);

  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
  };

  const getTransactionStatus = (status: string): string => {
    switch (status) {
      case 'done':
        return strings.displayText.completedText;
      case 'processing':
        return strings.displayText.processingText;
      default:
        return status;
    }
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: transactionDetailsConstants.dateField,
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.transactionDetailsDateColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row.transactionDate)}
        </Typography>
      ),
    },
    {
      field: transactionDetailsConstants.filledDateField,
      width: 110,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.filledDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.filledDate ? dateFormat(row.filledDate) : '-'}
        </Typography>
      ),
    },
    {
      field: transactionDetailsConstants.settlementDateField,
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.settlementDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.settlementDate ? dateFormat(row.settlementDate) : '-'}
        </Typography>
      ),
    },
    {
      field: transactionDetailsConstants.fundName,
      width: 600,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row?.fundDescription} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row?.shortDescription}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: transactionDetailsConstants.fundType,
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundType}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {getFundType(row.fundType)}
        </Typography>
      ),
    },

    {
      field: transactionDetailsConstants.totalContributionField,
      width: 150,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.transactionDetailsTotalContributionColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {CurrencyFormater(Number(row.totalContribution))}
        </Typography>
      ),
    },
    {
      field: transactionDetailsConstants.totalUnits,
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.totalUnits}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          <a
            style={{ color: 'blue', textDecoration: 'underline' }}
            onClick={() => {
              setSelectedOrderId(row?.mmOrderId);
              setIsModalOpen(true);
            }}
          >
            {UnitFormater(Number(row.totalUnits))}
          </a>
        </Typography>
      ),
    },
    {
      field: transactionDetailsConstants.orderTypeField,
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.transactionDetailsOrderTypeColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.transactionMethod}
        </Typography>
      ),
    },
    {
      field: transactionDetailsConstants.statusField,
      width: 100,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.transactionDetailsStatusColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {getTransactionStatus(row.transactionStatus)}
        </Typography>
      ),
    },
  ];
  const handleChatDialogClose = (): void => {
    setIsModalOpen(false);
    dispatch(
      setCurrentPage({
        key: 'userAllocationInfo',
        value: 0,
      }),
    );
  };
  const handleFilterChange = (filterType: string, value: string): void => {
    setFilters({
      ...filters,
      [filterType]: value,
    });
  };
  const handleApplyFilter = async (): Promise<void> => {
    dispatch(setCurrentPage({ key: 'mmTransaction', value: 0 }));

    void handleFetchTransDetails();
  };

  return (
    <>
      <Grid container spacing={2} alignItems="center">
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.transStartDate}
            type="date"
            value={filters.startDate}
            onChange={(e) => {
              handleFilterChange('startDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.transEndDate}
            type="date"
            value={filters.endDate}
            onChange={(e) => {
              handleFilterChange('endDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.filledStartDate}
            type="date"
            value={filters.fStartDate}
            onChange={(e) => {
              handleFilterChange('fStartDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.filledEndDate}
            type="date"
            value={filters.fEndDate}
            onChange={(e) => {
              handleFilterChange('fEndDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.settleStartDate}
            type="date"
            value={filters.sStartDate}
            onChange={(e) => {
              handleFilterChange('sStartDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid sx={{ minWidth: 100, marginTop: 2, marginRight: 1 }}>
          <TextField
            label={strings.displayText.settleEndDate}
            type="date"
            value={filters.sEndDate}
            onChange={(e) => {
              handleFilterChange('sEndDate', e.target.value);
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid item xs={1} />
        <Grid sx={{ minWidth: 300, marginTop: 2, marginRight: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Fund Type</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="Fund Type"
              value={filters.fundType}
              onChange={(e) => {
                handleFilterChange('fundType', e.target.value);
              }}
            >
              {fundTypeOption.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid sx={{ minWidth: 300, marginTop: 2, marginRight: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Order Type</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="Order Type"
              value={filters.orderType}
              onChange={(e) => {
                handleFilterChange('orderType', e.target.value);
              }}
            >
              {orderTypeOption.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid sx={{ minWidth: 300, marginTop: 2, marginRight: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">Status</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select-Status"
              label="Status"
              value={filters.status}
              onChange={(e) => {
                handleFilterChange('status', e.target.value);
              }}
            >
              {transStatusTypeOption.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <LoadingButton
            type="submit"
            fullWidth
            variant="contained"
            loadingPosition="end"
            onClick={() => {
              void handleApplyFilter();
            }}
          >
            {strings.displayText.filter}
          </LoadingButton>
        </Grid>
      </Grid>
      <CustomDataGrid
        columns={columns}
        rows={planData as GridRowData[]}
        getRowId={(row: GridRowData) => `${String(row.mmOrderTransactionId)}`}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        currentPage="mmTransaction"
        pageCount={pageCount}
        searchEnabled={true}
        handleSearch={handleSearch}
        searchPlaceholder={strings.displayText.searchLabel5}
        sortModel={checkSortingValues(pageInfo)}
        sortingMode={strings.displayText.sortingServer}
        onSortModelChange={(mode) => {
          dispatch(
            setSortBy({
              key: 'mmTransaction',
              value: mode[0].field,
            }),
          );
          dispatch(
            setSortOrder({
              key: 'mmTransaction',
              value: mode[0].sort,
            }),
          );
          dispatch(setCurrentPage({ key: 'mmTransaction', value: 0 }));
        }}
      />
      <DialogBox
        open={isModalOpen}
        onClose={handleChatDialogClose}
        title="Allocated Units User Details"
        fullWidth={true}
        screenWidth="xl"
      >
        <AllocationDetails orderId={selectedOrderId} />
      </DialogBox>
    </>
  );
};

export default TransactionDetails;
